import {useHomeAssignments} from '@features/home/queries/useHomeAssignments.ts'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {AssignmentsURLParams} from '@features/home/types.ts'
import {DataTable} from '@components/commons/data-table'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon, ArrowRightIcon} from '@components/ui/icon'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {Assignment, CoordinationStatus} from '@features/assignment/types.ts'
import {generatePath, useLocation} from 'react-router-dom'
import {routes} from '@utilities/constants/routes.tsx'
import {Tooltip} from '@components/ui/tooltip'
import {ListingTableCellInfo} from '@features/home/components/listing-table-cell-info'
import {HomeTableEmptyState} from '@features/home/components/home-table-empty-state'
import {HomeTableEmptySearch} from '@features/home/components/home-table-empty-search'
import {MobileTable} from '@components/commons/mobile-table.tsx'
import {useMediaQuery} from '@hooks/use-media-query'
import {PrimaryMobileTableRow, TableAreaItem} from '@features/home/components/home-atoms'
import {TABLE_ELEMENT_LIMIT} from '@features/home/utils.tsx'
import {StatusDot} from '@features/home/components/status-dot'
import {ButtonLink} from '@components/commons/button-link'
import {formatHostAvailabilityDates, sortHostAvailabilityDates} from '@features/assignment/utils'

const coordinationStatusToLabel = {
    host_unresponsive: 'dashboard:calendar:table:coordination_statuses:host_unresponsive',
    host_not_ready: 'dashboard:calendar:table:coordination_statuses:host_not_ready',
    host_dates_shared: 'dashboard:calendar:table:coordination_statuses:host_dates_shared',
    photographer_not_available: 'dashboard:calendar:table:coordination_statuses:photographer_not_available',
    dates_dont_match: 'dashboard:calendar:table:coordination_statuses:dates_dont_match',
    manual_coordination: 'dashboard:calendar:table:coordination_statuses:manual_coordination',
    overdue_host_contacted: 'dashboard:calendar:table:coordination_statuses:overdue_host_contacted',
    host_contacted: 'dashboard:calendar:table:coordination_statuses:host_contacted',
    cancellation_requested: 'dashboard:calendar:table:coordination_statuses:cancellation_requested'
} as const satisfies Record<CoordinationStatus, TKey>

export const CoordinationTable = ({onClearSearch}: {onClearSearch: () => void}) => {
    const {t} = useTranslation()
    const {search} = useLocation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const assignmentsQuery = useHomeAssignments(searchParams)
    const isLarge = useMediaQuery({breakpoint: 'md'})

    const renderDotFromStatus = (status: CoordinationStatus) => {
        switch (status) {
            case 'overdue_host_contacted':
                return <StatusDot variant={'danger'} />
            case 'manual_coordination':
            case 'host_dates_shared':
                return <StatusDot variant={'warning'} />
            default:
                return <StatusDot variant={'blue'} />
        }
    }

    return (
        <div className="flex flex-col gap-4 w-full items-stretch">
            <p>{t('home:table:titles:assignments_need_scheduled')}</p>
            {assignmentsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : assignmentsQuery.data?.data.length == 0 && !searchParams.search ? (
                <HomeTableEmptyState />
            ) : !isLarge ? (
                <MobileTable
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    emptyStateComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    rows={[
                        {
                            id: 'listing',
                            cell: ({dataItem}) => (
                                <PrimaryMobileTableRow>
                                    <div className="flex flex-col gap-2">
                                        <ListingTableCellInfo item={dataItem} />
                                        {dataItem.task_status.coordination_status ? (
                                            <div className="flex gap-2 items-center">
                                                {renderDotFromStatus(dataItem.task_status.coordination_status)}
                                                <TableBodyCellText variant={'dark'}>
                                                    {dataItem.task_status.coordination_status &&
                                                        t(
                                                            coordinationStatusToLabel[
                                                                dataItem.task_status.coordination_status
                                                            ]
                                                        )}
                                                </TableBodyCellText>
                                            </div>
                                        ) : (
                                            '-'
                                        )}
                                    </div>
                                    <ButtonLink
                                        to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        })}
                                        size={'sm'}
                                        variant={'tertiary'}
                                    >
                                        <ArrowRightIcon />
                                    </ButtonLink>
                                </PrimaryMobileTableRow>
                            ),
                            header: () => <></>
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: () => <TableBodyCellText variant={'normal'}>{t('commons:area')}</TableBodyCellText>,
                            id: 'area'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: () => (
                                <TableBodyCellText variant={'normal'}>
                                    {t('dashboard:calendar:table:host')}
                                </TableBodyCellText>
                            ),
                            id: 'host'
                        },
                        {
                            cell: ({dataItem}) => {
                                const formattedDates = !dataItem.quote
                                    ? null
                                    : sortHostAvailabilityDates(dataItem.quote.host_availability_dates).map(date =>
                                          formatHostAvailabilityDates(date.starting_time, date.ending_time)
                                      )

                                return (
                                    <TableBodyCellText variant={'dark'} className="wrap-anywhere whitespace-normal">
                                        {!dataItem.quote || dataItem.quote.host_availability_dates.length == 0 ? (
                                            '-'
                                        ) : formattedDates && formattedDates.length > 2 ? (
                                            <>
                                                {' '}
                                                {formattedDates[0]}{' '}
                                                <Tooltip
                                                    content={
                                                        <ul>
                                                            {formattedDates.slice(1).map(date => (
                                                                <li key={date}>{date}</li>
                                                            ))}
                                                        </ul>
                                                    }
                                                >
                                                    <div>
                                                        <span className="text-neutral-700 underline cursor-pointer">
                                                            {` + ${formattedDates.length - 1} ${t('home:table:proposed_dates').toLocaleLowerCase()}`}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            formattedDates?.join(' - ') ?? '-'
                                        )}
                                    </TableBodyCellText>
                                )
                            },
                            header: () => (
                                <TableBodyCellText variant={'normal'}>
                                    {t('home:table:proposed_dates')}
                                </TableBodyCellText>
                            ),
                            id: 'proposed_dates'
                        }
                    ]}
                />
            ) : (
                <DataTable
                    emptyComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    columnPinning={{right: ['cta'], left: ['listing']}}
                    order={{
                        orderBy: assignmentsQuery.sort.sortName ?? undefined,
                        orderDirection: assignmentsQuery.sort.direction ?? undefined
                    }}
                    setOrder={nextOrder =>
                        assignmentsQuery.sort.onSort(nextOrder?.orderBy ?? null, nextOrder?.orderDirection ?? null)
                    }
                    columns={[
                        {
                            cell: ({dataItem}: {dataItem: Assignment}) => <ListingTableCellInfo item={dataItem} />,
                            header: t('commons:listing').toUpperCase(),
                            id: 'listing',
                            size: 190,
                            sortKey: 'title'
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: t('commons:area').toUpperCase(),
                            id: 'area',
                            size: 200,
                            sortKey: 'address'
                        },
                        {
                            cell: ({dataItem}: {dataItem: Assignment}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: t('dashboard:calendar:table:host').toUpperCase(),
                            id: 'host',
                            size: 150,
                            sortKey: 'first_name'
                        },
                        {
                            cell: ({dataItem}: {dataItem: Assignment}) => {
                                const formattedDates = !dataItem.quote
                                    ? null
                                    : sortHostAvailabilityDates(dataItem.quote.host_availability_dates).map(date =>
                                          formatHostAvailabilityDates(date.starting_time, date.ending_time)
                                      )

                                return (
                                    <TableBodyCellText variant={'dark'} className="wrap-anywhere whitespace-normal">
                                        {!dataItem.quote || dataItem.quote.host_availability_dates.length == 0 ? (
                                            '-'
                                        ) : formattedDates && formattedDates.length > 2 ? (
                                            <>
                                                {' '}
                                                {formattedDates[0]}{' '}
                                                <Tooltip
                                                    content={
                                                        <ul>
                                                            {formattedDates.slice(1).map(date => (
                                                                <li key={date}>{date}</li>
                                                            ))}
                                                        </ul>
                                                    }
                                                >
                                                    <div>
                                                        <span className="text-neutral-700 underline cursor-pointer">
                                                            {` + ${formattedDates.length - 1} ${t('home:table:proposed_dates').toLocaleLowerCase()}`}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            formattedDates?.join(' - ')
                                        )}
                                    </TableBodyCellText>
                                )
                            },
                            header: t('home:table:proposed_dates').toUpperCase(),
                            id: 'proposed_dates',
                            size: 320,
                            sortKey: 'host_availability_dates'
                        },
                        {
                            cell: ({dataItem}: {dataItem: Assignment}) =>
                                dataItem.task_status.coordination_status ? (
                                    <div className="flex gap-2 items-center">
                                        {renderDotFromStatus(dataItem.task_status.coordination_status)}
                                        <TableBodyCellText variant={'dark'}>
                                            {dataItem.task_status.coordination_status &&
                                                t(coordinationStatusToLabel[dataItem.task_status.coordination_status])}
                                        </TableBodyCellText>
                                    </div>
                                ) : (
                                    '-'
                                ),
                            header: t('dashboard:calendar:table:status').toUpperCase(),
                            id: 'coordination_status',
                            size: 220,
                            sortKey: 'coordination_status'
                        },
                        {
                            cell: ({dataItem}: {dataItem: Assignment}) => (
                                <ButtonLink
                                    to={{
                                        pathname: generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        }),
                                        search
                                    }}
                                    size={'sm'}
                                    variant={'tertiary'}
                                >
                                    {t('commons:open')}
                                </ButtonLink>
                            ),
                            header: <></>,
                            id: 'cta',
                            size: 80
                        }
                    ]}
                />
            )}
        </div>
    )
}
